'use client'
import { useMobileHeader }         from './useMobileHeader'
import type { MobileHeaderIconType } from './MobileHeader.types'

function ActionIcon({ type }: { type: MobileHeaderIconType }) {
  switch (type) {
    case 'share':
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
          <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
          <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" /><line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
        </svg>
      )
    case 'download':
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="12" y1="18" x2="12" y2="12" /><polyline points="9 15 12 18 15 15" />
        </svg>
      )
    case 'filter':
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
          <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
        </svg>
      )
    case 'qr':
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="3" y="14" width="7" height="7" />
          <rect x="14" y="14" width="3" height="3" /><rect x="18" y="14" width="3" height="3" /><rect x="14" y="18" width="3" height="3" /><rect x="18" y="18" width="3" height="3" />
        </svg>
      )
    case 'search':
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
          <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
      )
    case 'close':
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
          <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      )
    case 'edit':
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
        </svg>
      )
    case 'more':
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="5" r="1" /><circle cx="12" cy="12" r="1" /><circle cx="12" cy="19" r="1" />
        </svg>
      )
    default:
      return null
  }
}

export function MobileHeader() {
  const vm = useMobileHeader()

  if (vm.hidden) return null

  // Invisible spacer — keeps title centered when there are fewer than 2 actions.
  const spacers = Math.max(0, 2 - vm.actions.length)

  return (
    <header className="flex lg:hidden items-center justify-between h-12 px-3 shrink-0 border-b border-[var(--color-border)] bg-[var(--color-surface)]">

      {/* Back button */}
      <button
        type="button"
        onClick={vm.handleBack}
        aria-label={vm.backLabel}
        className="w-9 h-9 rounded-[10px] flex items-center justify-center text-[var(--color-text-primary)] hover:bg-[var(--color-surface-control)] transition-colors shrink-0"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
          <line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" />
        </svg>
      </button>

      {/* Title */}
      <span className="flex-1 text-center text-sm font-semibold text-[var(--color-text-primary)] truncate px-2">
        {vm.title}
      </span>

      {/* Right actions (max 2) + spacers to keep title centered */}
      <div className="flex items-center gap-1.5 shrink-0">
        {vm.actions.map(action => (
          <button
            key={action.label}
            type="button"
            onClick={action.onClick}
            disabled={action.disabled || action.loading}
            aria-label={action.label}
            title={action.label}
            className="w-9 h-9 rounded-[10px] bg-[var(--color-accent)] text-white flex items-center justify-center hover:opacity-90 transition-opacity disabled:opacity-50 shrink-0"
          >
            {action.loading
              ? <span className="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
              : <ActionIcon type={action.icon} />
            }
          </button>
        ))}
        {Array.from({ length: spacers }).map((_, i) => (
          <div key={i} className="w-9 shrink-0" />
        ))}
      </div>

    </header>
  )
}
